import jsPDF from "jspdf";
import { supabase } from "../../lib/supabase";
import { fetchInvoiceSettings } from "./invoiceEngine";

const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_neon-beats-label/artifacts/as4oc6zo_INDEPENDENT_MUSIC_LABEL__1_-removebg-preview.png";
let _logoCache = null;
async function loadLogoDataUrl() {
  if (_logoCache) return _logoCache;
  try {
    const resp = await fetch(LOGO_URL, { mode: "cors" });
    const blob = await resp.blob();
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
    _logoCache = dataUrl;
    return dataUrl;
  } catch { return null; }
}

const formatEUR = (v) => (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

// ═══════════════════════════════════════════════════════════════
// UStVA-Berechnung aus Monatsdaten
// ═══════════════════════════════════════════════════════════════
export function computeUstva(data) {
  // Gruppieren nach Steuersatz
  const outByRate = {}; // { 19: { net, tax }, 7: { net, tax }, 0: { net, tax } }
  const inByRate = {};

  const bucket = (bag, rate, net, tax) => {
    const r = Number(rate || 0);
    (bag[r] ||= { net: 0, tax: 0 });
    bag[r].net += Number(net || 0);
    bag[r].tax += Number(tax || 0);
  };

  for (const i of (data.outgoing || [])) bucket(outByRate, i.tax_rate, i.subtotal, i.tax_amount);
  for (const i of (data.incoming || [])) bucket(inByRate, i.tax_rate, i.subtotal, i.tax_amount);

  const totalUmsatz19 = (outByRate[19]?.net) || 0;
  const totalUst19 = (outByRate[19]?.tax) || 0;
  const totalUmsatz7 = (outByRate[7]?.net) || 0;
  const totalUst7 = (outByRate[7]?.tax) || 0;
  const totalUmsatzOther = Object.entries(outByRate).filter(([r]) => r !== "19" && r !== "7" && r !== "0").reduce((s, [, v]) => s + v.net, 0);
  const totalUstOther = Object.entries(outByRate).filter(([r]) => r !== "19" && r !== "7" && r !== "0").reduce((s, [, v]) => s + v.tax, 0);
  const totalUmsatzFrei = (outByRate[0]?.net) || 0;

  const totalVorsteuer = Object.values(inByRate).reduce((s, v) => s + v.tax, 0);

  const ustSumme = totalUst19 + totalUst7 + totalUstOther;
  const zahllast = ustSumme - totalVorsteuer;

  return {
    umsatz19: totalUmsatz19,
    ust19: totalUst19,
    umsatz7: totalUmsatz7,
    ust7: totalUst7,
    umsatzOther: totalUmsatzOther,
    ustOther: totalUstOther,
    umsatzFrei: totalUmsatzFrei,
    vorsteuer: totalVorsteuer,
    ustSumme,
    zahllast,
    outByRate,
    inByRate,
  };
}

// ═══════════════════════════════════════════════════════════════
// PDF-Rendering: kompakter UStVA-Export
// ═══════════════════════════════════════════════════════════════
export async function renderUstvaPdf(month, data) {
  const u = computeUstva(data);
  const settings = await fetchInvoiceSettings().catch(() => ({}));
  const logoData = await loadLogoDataUrl();

  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 20;
  const bannerH = 40;
  const contentW = pageW - 2 * marginX;

  const INK = [24, 24, 28];
  const MUTED = [110, 110, 118];
  const LINE = [220, 220, 224];
  const BORDEAUX = [128, 24, 38];
  const ACCENT = [180, 30, 42];
  const SOFT = [245, 245, 247];

  // Banner
  pdf.setFillColor(...BORDEAUX);
  pdf.rect(0, 0, pageW, bannerH, "F");
  if (logoData) {
    try { const lw = 76, lh = 30; pdf.addImage(logoData, "PNG", (pageW - lw) / 2, (bannerH - lh) / 2, lw, lh); } catch {}
  }

  let y = bannerH + 14;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(19);
  pdf.setTextColor(...INK);
  pdf.text("UStVA-EXPORT", marginX, y);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  const [yr, mo] = month.split("-");
  const monthLabel = new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  pdf.text(`Umsatzsteuer-Voranmeldung · ${monthLabel}`, marginX, y + 5);

  pdf.setDrawColor(...ACCENT);
  pdf.setLineWidth(0.8);
  pdf.line(marginX, y + 2, marginX + 42, y + 2);
  pdf.setLineWidth(0.2);

  // Absender rechts
  pdf.setFontSize(7.5);
  pdf.setTextColor(...MUTED);
  const senderLines = [
    settings.company_name || "Straßenwert Records",
    settings.owner_name,
    settings.address_street,
    [settings.address_zip, settings.address_city].filter(Boolean).join(" "),
    settings.tax_id ? `St.-Nr.: ${settings.tax_id}` : null,
    settings.vat_id ? `USt-IdNr.: ${settings.vat_id}` : null,
  ].filter(Boolean);
  senderLines.forEach((ln, i) => pdf.text(ln, pageW - marginX, y - 4 + i * 3.5, { align: "right" }));

  y += 22;

  // Hinweistext
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...INK);
  const intro = `Diese Übersicht enthält die für die Umsatzsteuer-Voranmeldung in ELSTER benötigten Werte. Trage die Beträge in die angegebenen Kennzahlen im ELSTER-Formular "Umsatzsteuer-Voranmeldung" ein.`;
  const introLines = pdf.splitTextToSize(intro, contentW);
  for (const l of introLines) { pdf.text(l, marginX, y); y += 4.8; }
  y += 4;

  // Tabelle
  const drawSectionHeader = (title) => {
    pdf.setFillColor(...SOFT);
    pdf.rect(marginX, y - 4, contentW, 7, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...INK);
    pdf.text(title, marginX + 4, y);
    pdf.text("KENNZAHL", marginX + 100, y);
    pdf.text("BETRAG", pageW - marginX - 4, y, { align: "right" });
    y += 5;
  };

  const drawRow = (label, kz, amount, opts = {}) => {
    pdf.setDrawColor(...LINE);
    pdf.setLineWidth(0.15);
    pdf.line(marginX, y + 2, pageW - marginX, y + 2);
    pdf.setFont("helvetica", opts.bold ? "bold" : "normal");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...(opts.muted ? MUTED : INK));
    pdf.text(label, marginX + 4, y);
    if (kz) {
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...ACCENT);
      pdf.text(kz, marginX + 100, y);
    }
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...(opts.accent ? ACCENT : INK));
    pdf.text(formatEUR(amount), pageW - marginX - 4, y, { align: "right" });
    y += 7;
  };

  // Sektion 1: Ausgangs-Umsätze
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...INK);
  pdf.text("Steuerpflichtige Umsätze (Ausgangsrechnungen)", marginX, y);
  y += 6;
  drawSectionHeader("BEZEICHNUNG");
  drawRow("Bemessungsgrundlage 19 %", "81", u.umsatz19);
  drawRow("Umsatzsteuer 19 %", "", u.ust19, { muted: true });
  if (u.umsatz7 > 0) {
    drawRow("Bemessungsgrundlage 7 %", "86", u.umsatz7);
    drawRow("Umsatzsteuer 7 %", "", u.ust7, { muted: true });
  }
  if (u.umsatzOther > 0) {
    drawRow("Bemessungsgrundlage (sonstiger Satz)", "35", u.umsatzOther);
    drawRow("Umsatzsteuer (sonstiger Satz)", "", u.ustOther, { muted: true });
  }
  y += 4;

  // Sektion 2: Vorsteuer
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...INK);
  pdf.text("Vorsteuer (Eingangsrechnungen)", marginX, y);
  y += 6;
  drawSectionHeader("BEZEICHNUNG");
  drawRow("Abziehbare Vorsteuerbeträge", "66", u.vorsteuer);
  y += 4;

  // Sektion 3: Zusammenfassung
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...INK);
  pdf.text("Zusammenfassung", marginX, y);
  y += 6;
  drawSectionHeader("BEZEICHNUNG");
  drawRow("Summe Umsatzsteuer", "", u.ustSumme);
  drawRow("abzgl. Vorsteuer", "", -u.vorsteuer, { muted: true });

  // Zahllast-Zeile
  y += 2;
  pdf.setDrawColor(...INK);
  pdf.setLineWidth(0.5);
  pdf.line(marginX, y - 3, pageW - marginX, y - 3);
  pdf.setFillColor(255, 240, 240);
  pdf.rect(marginX, y - 2, contentW, 12, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...ACCENT);
  pdf.text(u.zahllast >= 0 ? "Zahllast (an Finanzamt)" : "Erstattung (vom Finanzamt)", marginX + 4, y + 4);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...ACCENT);
  pdf.text("83", marginX + 100, y + 4);
  pdf.setFontSize(13);
  pdf.text(formatEUR(Math.abs(u.zahllast)), pageW - marginX - 4, y + 4, { align: "right" });
  y += 20;

  // Rechtlicher Hinweis
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  const hint = `Hinweis: Diese Übersicht wurde automatisch aus den im Straßenwert-Drive erfassten Rechnungen berechnet. Sie ersetzt keine Steuerberatung. Prüfe die Werte vor der Übermittlung an das Finanzamt sorgfältig. Nicht in dieser Berechnung enthalten: MusicHub-Auszahlungen (falls sie im Rahmen der Kleinunternehmerregelung deines Vertragspartners abgerechnet werden), reverse-charge-Umsätze und Auslandsumsätze.`;
  const hintLines = pdf.splitTextToSize(hint, contentW);
  for (const l of hintLines) { pdf.text(l, marginX, y); y += 4; }

  // Fußzeile
  const fy = pageH - 15;
  pdf.setDrawColor(...LINE);
  pdf.setLineWidth(0.25);
  pdf.line(marginX, fy - 6, pageW - marginX, fy - 6);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...MUTED);
  pdf.text(`${settings.company_name || "Straßenwert Records"} · UStVA-Export ${month} · Erstellt am ${new Date().toLocaleDateString("de-DE")}`, marginX, fy);
  pdf.text("Seite 1 / 1", pageW - marginX, fy, { align: "right" });

  return pdf;
}

// CSV-Export für Archiv
export function buildUstvaCsv(month, data) {
  const u = computeUstva(data);
  const rows = [
    ["Monat", month],
    ["Kennzahl", "Bezeichnung", "Betrag (EUR)"],
    ["81", "Bemessungsgrundlage 19%", u.umsatz19.toFixed(2)],
    ["—", "Umsatzsteuer 19%", u.ust19.toFixed(2)],
    ["86", "Bemessungsgrundlage 7%", u.umsatz7.toFixed(2)],
    ["—", "Umsatzsteuer 7%", u.ust7.toFixed(2)],
    ["66", "Vorsteuer", u.vorsteuer.toFixed(2)],
    ["83", "Zahllast an Finanzamt", u.zahllast.toFixed(2)],
  ];
  return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
}

// ═══════════════════════════════════════════════════════════════
// EÜR-Jahresübersicht (Einnahmen-Überschuss-Rechnung)
// ═══════════════════════════════════════════════════════════════
export async function fetchEuerData(year) {
  const start = `${year}-01`, end = `${year}-12`;
  const [inv, mh, prov] = await Promise.all([
    supabase.from("drive_invoices").select("*").gte("contract_month", start).lte("contract_month", end),
    supabase.from("drive_music_hub_payouts").select("*").gte("month", start).lte("month", end).then(r => ({ data: r.error ? [] : (r.data || []) })),
    supabase.from("drive_provision_statements").select("*").gte("month", start).lte("month", end).then(r => ({ data: r.error ? [] : (r.data || []) })),
  ]);
  if (inv.error) throw inv.error;
  const outgoing = (inv.data || []).filter(i => i.kind === "ausgang");
  const incoming = (inv.data || []).filter(i => i.kind === "eingang");

  // Monatliches Aggregat
  const months = {};
  for (let m = 1; m <= 12; m++) {
    const key = `${year}-${String(m).padStart(2, "0")}`;
    months[key] = { einnahmen: 0, ausgaben: 0, ust: 0, vorsteuer: 0 };
  }

  for (const i of outgoing) {
    const k = i.contract_month;
    if (months[k]) {
      months[k].einnahmen += Number(i.subtotal || 0);
      months[k].ust += Number(i.tax_amount || 0);
    }
  }
  for (const i of incoming) {
    const k = i.contract_month;
    if (months[k]) {
      months[k].ausgaben += Number(i.subtotal || 0);
      months[k].vorsteuer += Number(i.tax_amount || 0);
    }
  }
  // MusicHub-Auszahlungen als Einnahmen (Netto — Annahme: kein USt-Ausweis)
  for (const p of (mh.data || [])) {
    const k = p.month;
    if (months[k]) months[k].einnahmen += Number(p.amount || 0);
  }
  // Provisionszahlungen an Künstler als Ausgaben
  for (const p of (prov.data || [])) {
    const k = p.month;
    if (months[k]) months[k].ausgaben += Number(p.artist_amount || 0);
  }

  const totals = Object.values(months).reduce((s, m) => ({
    einnahmen: s.einnahmen + m.einnahmen,
    ausgaben: s.ausgaben + m.ausgaben,
    ust: s.ust + m.ust,
    vorsteuer: s.vorsteuer + m.vorsteuer,
  }), { einnahmen: 0, ausgaben: 0, ust: 0, vorsteuer: 0 });
  totals.gewinn = totals.einnahmen - totals.ausgaben;
  totals.ustZahllast = totals.ust - totals.vorsteuer;

  return { year, months, totals };
}

export async function renderEuerPdf(year) {
  const d = await fetchEuerData(year);
  const settings = await fetchInvoiceSettings().catch(() => ({}));
  const logoData = await loadLogoDataUrl();

  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 20;
  const bannerH = 40;
  const contentW = pageW - 2 * marginX;

  const INK = [24, 24, 28];
  const MUTED = [110, 110, 118];
  const LINE = [220, 220, 224];
  const BORDEAUX = [128, 24, 38];
  const ACCENT = [180, 30, 42];
  const SOFT = [245, 245, 247];

  pdf.setFillColor(...BORDEAUX);
  pdf.rect(0, 0, pageW, bannerH, "F");
  if (logoData) { try { const lw = 76, lh = 30; pdf.addImage(logoData, "PNG", (pageW - lw) / 2, (bannerH - lh) / 2, lw, lh); } catch {} }

  let y = bannerH + 14;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(19);
  pdf.setTextColor(...INK);
  pdf.text("EÜR-JAHRESÜBERSICHT", marginX, y);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text(`Einnahmen-Überschuss-Rechnung · Steuerjahr ${year}`, marginX, y + 5);
  pdf.setDrawColor(...ACCENT); pdf.setLineWidth(0.8);
  pdf.line(marginX, y + 2, marginX + 42, y + 2);
  pdf.setLineWidth(0.2);

  pdf.setFontSize(7.5);
  pdf.setTextColor(...MUTED);
  const senderLines = [
    settings.company_name || "Straßenwert Records",
    settings.owner_name,
    settings.address_street,
    [settings.address_zip, settings.address_city].filter(Boolean).join(" "),
    settings.tax_id ? `St.-Nr.: ${settings.tax_id}` : null,
  ].filter(Boolean);
  senderLines.forEach((ln, i) => pdf.text(ln, pageW - marginX, y - 4 + i * 3.5, { align: "right" }));
  y += 22;

  // Monats-Tabelle
  pdf.setFillColor(...SOFT);
  pdf.rect(marginX, y - 4, contentW, 7, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7.5);
  pdf.setTextColor(...INK);
  const cX = { m: marginX + 4, ein: pageW - marginX - 90, aus: pageW - marginX - 60, gewinn: pageW - marginX - 4 };
  pdf.text("MONAT", cX.m, y);
  pdf.text("EINNAHMEN (NETTO)", cX.ein, y, { align: "right" });
  pdf.text("AUSGABEN (NETTO)", cX.aus, y, { align: "right" });
  pdf.text("GEWINN", cX.gewinn, y, { align: "right" });
  y += 5;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  let stripe = false;
  Object.entries(d.months).forEach(([m, v]) => {
    if (stripe) { pdf.setFillColor(250, 250, 251); pdf.rect(marginX, y - 3.5, contentW, 6.5, "F"); }
    stripe = !stripe;
    pdf.setTextColor(...INK);
    const [yr, mo] = m.split("-");
    pdf.text(new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString("de-DE", { month: "long" }), cX.m, y);
    pdf.text(formatEUR(v.einnahmen), cX.ein, y, { align: "right" });
    pdf.text(formatEUR(v.ausgaben), cX.aus, y, { align: "right" });
    const g = v.einnahmen - v.ausgaben;
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...(g >= 0 ? INK : ACCENT));
    pdf.text(formatEUR(g), cX.gewinn, y, { align: "right" });
    pdf.setFont("helvetica", "normal");
    y += 6.5;
  });

  // Summenzeile
  y += 2;
  pdf.setDrawColor(...INK); pdf.setLineWidth(0.5);
  pdf.line(marginX, y - 3, pageW - marginX, y - 3);
  pdf.setFillColor(255, 240, 240);
  pdf.rect(marginX, y - 2, contentW, 12, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...ACCENT);
  pdf.text(`GESAMT ${year}`, cX.m, y + 5);
  pdf.text(formatEUR(d.totals.einnahmen), cX.ein, y + 5, { align: "right" });
  pdf.text(formatEUR(d.totals.ausgaben), cX.aus, y + 5, { align: "right" });
  pdf.setFontSize(12);
  pdf.text(formatEUR(d.totals.gewinn), cX.gewinn, y + 5, { align: "right" });
  y += 20;

  // Zusammenfassung / USt
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...INK);
  pdf.text("Umsatzsteuer-Zusammenfassung", marginX, y);
  y += 7;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(...INK);
  pdf.text(`Umsatzsteuer eingenommen: ${formatEUR(d.totals.ust)}`, marginX, y); y += 5;
  pdf.text(`Vorsteuer aus Rechnungen: ${formatEUR(d.totals.vorsteuer)}`, marginX, y); y += 5;
  pdf.setFont("helvetica", "bold");
  pdf.text(`Jahres-Zahllast (bereits über UStVA gezahlt): ${formatEUR(d.totals.ustZahllast)}`, marginX, y); y += 8;

  // Rechtlicher Hinweis
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  const hint = `Diese Übersicht dient als Vorbereitung deiner EÜR-Erklärung für ELSTER. Sie berücksichtigt alle im Straßenwert-Drive erfassten Ausgangs-/Eingangsrechnungen, MusicHub-Auszahlungen (als Einnahme) und Provisionszahlungen an Künstler (als Ausgabe). Prüfe die Werte vor der Übermittlung sorgfältig. Für die tatsächliche EÜR-Erklärung in ELSTER musst du weitere Positionen berücksichtigen (Anlagevermögen, Abschreibungen, Reisekosten etc.), die nicht in diesem System erfasst sind.`;
  const hintLines = pdf.splitTextToSize(hint, contentW);
  for (const l of hintLines) { pdf.text(l, marginX, y); y += 4; }

  // Fußzeile
  const fy = pageH - 15;
  pdf.setDrawColor(...LINE); pdf.setLineWidth(0.25);
  pdf.line(marginX, fy - 6, pageW - marginX, fy - 6);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...MUTED);
  pdf.text(`${settings.company_name || "Straßenwert Records"} · EÜR ${year} · Erstellt am ${new Date().toLocaleDateString("de-DE")}`, marginX, fy);
  pdf.text("Seite 1 / 1", pageW - marginX, fy, { align: "right" });

  return pdf;
}
